import React, { useState } from 'react';
import styled from 'styled-components';
import { PostCampaignSelector } from '~/shared-components/PostCampaignSelector';
import { RootState, useAppSelector } from '~/store';
import { selectPostById } from '~/post/slice';
import { ThreadsFreeOBPaywall } from '~/composer/composer/components/ThreadsFreeOBPaywall';
import FlashIcon from '@bufferapp/ui/Icon/Icons/Flash';
import { useShowThreadsUpgrade } from '~/utils/canQueueMoreThreadsForAllTwitterProfiles';
import { SERVICE_YOUTUBE } from '~/constants';
import { useSentPostActions } from '~/post/hooks/useSentPostActions';
import { selectProfiles } from '~/profile-sidebar/selectors';
import { PublishingPost } from '~/post/types.generated';
import { RpcUpdate } from '~/post/types';
import { selectCampaignTab } from '~/campaign/selectors';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { useAccount } from '~/accountContext';
import { ButtonStyled } from '../../CardFooter/styles';

type PropType = {
  postId: string;
  onShareAgainClick: (args?: { post?: PublishingPost | RpcUpdate }) => void;
  onShareLinkClick: (args?: {
    post?: PublishingPost | RpcUpdate;
    selectedProfileIds?: string[];
  }) => void;
  serviceLink: string;
};

const Container = styled.div`
  display: flex;
  button svg {
    width: 16px;
    height: 16px;
  }
`;

export const SentPostFooterActions = ({
  postId,
  onShareAgainClick,
  onShareLinkClick,
  serviceLink,
}: PropType): JSX.Element => {
  const { account } = useAccount();
  const post = useAppSelector((state: RootState) =>
    selectPostById(state, postId)
  );
  const campaignTab = useAppSelector(selectCampaignTab);
  const profiles = useAppSelector(selectProfiles);
  // eslint-disable-next-line dot-notation
  const selectedProfileIds = profiles.map((profile) => profile['id']);

  const onShareLinkClickWithArgs = () =>
    onShareLinkClick({
      post,
      selectedProfileIds,
    });

  const onShareAgainClickWithArgs = () => onShareAgainClick({ post });

  const { stackedActions } = useSentPostActions({
    post,
    onShareLinkClick: onShareLinkClickWithArgs,
    onShareAgainClick: onShareAgainClickWithArgs,
  });

  const [openModal, setOpenModal] = useState(false);

  const { shouldShowThreadsMigrationModal } = useShowThreadsUpgrade(post);

  const isYoutubePostWithServiceLink = () => {
    if (!post?.serviceLink) return false;

    if (
      'profile_service' in post &&
      post?.profile_service === SERVICE_YOUTUBE
    ) {
      return true;
    }

    if ('channel' in post && post?.channel?.service === SERVICE_YOUTUBE) {
      return true;
    }
  };

  const closeModal = () => {
    setOpenModal(false);
  };
  return (
    <Container>
      {serviceLink && (
        <ButtonStyled
          type="text"
          size="small"
          label="View Post"
          onClick={() => {
            window.open(serviceLink, '_blank');
          }}
        />
      )}
      <PostCampaignSelector postId={postId} size="small" />
      {(() => {
        if (shouldShowThreadsMigrationModal) {
          const commonTrackingProps =
            account?.currentOrganization?.commonTrackingProperties || null;

          BufferTracker.ctaViewed({
            organizationId: account?.currentOrganization?.id || null,
            ctaName: `publish-sendPosts-post-upgradeToScheduleThread-1`,
            upgradePathName: 'threadsLimit-upgrade',
            product: 'publish',
            ...commonTrackingProps,
          });
          return (
            <ButtonStyled
              data-testid="threads-free-paywall-button"
              type="primary"
              size="small"
              icon={<FlashIcon color="white" />}
              label="Upgrade to Schedule Thread"
              onClick={() => {
                BufferTracker.ctaClicked({
                  organizationId: account?.currentOrganization?.id || '',
                  ctaName: `publish-sendPosts-post-upgradeToScheduleThread-1`,
                  cta: `publish-sendPosts-post-upgradeToScheduleThread-1`,
                  upgradePathName: 'threadsLimit-upgrade',
                  product: 'publish',
                  clientName: 'publishWeb',
                  ...commonTrackingProps,
                });
                setOpenModal(true);
              }}
            />
          );
        }

        if (isYoutubePostWithServiceLink() && !campaignTab) {
          const [firstAction, ...restStackedActions] = stackedActions;
          const executeStackedActions = ({ id }) => {
            const selectedAction = stackedActions.find(
              (action) => action.id === id
            );
            if (selectedAction) selectedAction.callback();
          };

          return (
            <ButtonStyled
              type="secondary"
              size="small"
              isSplit={!!restStackedActions}
              items={restStackedActions}
              label={firstAction.title}
              onClick={firstAction.callback}
              onSelectClick={executeStackedActions}
            />
          );
        }

        return (
          <ButtonStyled
            type="secondary"
            size="small"
            label="Share Again"
            onClick={() => onShareAgainClick({ post })}
          />
        );
      })()}
      {openModal && (
        <ThreadsFreeOBPaywall
          closeFirstModal={closeModal}
          ctaString="sentPosts-upgradeButton-upgradeToScheduleThread-1"
        />
      )}
    </Container>
  );
};
